import { PDFDocument, PDFFont, PDFPage, StandardFonts } from 'pdf-lib';

import { enhancedApi } from '@/services/api/documentGenerationService';
import { store } from '@/state/state';

import { PdfFormTemplateField } from '../../types';

interface ModifyPdfProps {
  pdfBlob: Blob;
  placedFields: PdfFormTemplateField[];
  mergeData: Record<string, string | undefined>; // Map from field id to value
}

// Helper: compute line height (simple multiplier)
const getLineHeight = (fontSize: number, lineHeightFactor = 1.15) =>
  fontSize * lineHeightFactor;

// Helper: Greedy word-wrap. Returns wrapped lines **and** the widest line width.
const greedyWrapText = (
  text: string,
  maxWidth: number,
  font: PDFFont,
  fontSize: number,
): { lines: string[]; maxLineWidth: number } => {
  const paragraphs = text.split(/\n/g);
  const lines: string[] = [];
  let maxLineWidth = 0;

  paragraphs.forEach((paragraph, pIdx) => {
    const words = paragraph.split(' ');
    let currentLine = '';

    for (let i = 0; i < words.length; i += 1) {
      const word = words[i];
      const tentative = currentLine ? `${currentLine} ${word}` : word;
      const tentativeWidth = font.widthOfTextAtSize(tentative, fontSize);

      if (tentativeWidth > maxWidth && currentLine) {
        // push current line and start a new one with the word
        lines.push(currentLine);
        maxLineWidth = Math.max(
          maxLineWidth,
          font.widthOfTextAtSize(currentLine, fontSize),
        );
        currentLine = word;
      } else {
        currentLine = tentative;
      }
    }

    // Push last line in the paragraph
    if (currentLine) {
      lines.push(currentLine);
      maxLineWidth = Math.max(
        maxLineWidth,
        font.widthOfTextAtSize(currentLine, fontSize),
      );
    }

    // Preserve explicit paragraph breaks except for the last paragraph
    if (pIdx < paragraphs.length - 1) {
      lines.push('');
    }
  });

  return { lines, maxLineWidth };
};

// Attempts to wrap `text` inside the given dimensions, decreasing font size until it fits
// or `minFontSize` is reached. Returns the wrapped lines and the font size actually used.
const wrapText = (
  text: string,
  dimensions: { width: number; height: number },
  font: PDFFont,
  fontSize: number,
  minFontSize: number,
): { lines: string[]; fontSize: number } => {
  if (dimensions.width <= 0 || dimensions.height <= 0) {
    return { lines: [text], fontSize };
  }

  let currentSize = fontSize;
  let lastResult: { lines: string[]; maxLineWidth: number } = {
    lines: [text],
    maxLineWidth: font.widthOfTextAtSize(text, fontSize),
  };

  while (currentSize >= minFontSize) {
    const wrapResult = greedyWrapText(
      text,
      dimensions.width,
      font,
      currentSize,
    );
    const lineHeight = getLineHeight(currentSize);
    const totalHeight = wrapResult.lines.length * lineHeight;

    if (
      wrapResult.maxLineWidth <= dimensions.width &&
      totalHeight <= dimensions.height
    ) {
      return { lines: wrapResult.lines, fontSize: currentSize };
    }

    // store last attempt in case we never fit
    lastResult = wrapResult;
    currentSize -= 1;
  }

  // Best effort: return result at minFontSize even if overflowing
  return {
    lines: lastResult.lines,
    fontSize: Math.max(currentSize + 1, minFontSize),
  };
};

const parseDate = (dateStr: string): Date | null => {
  // Checked for numeric
  try {
    const dt = new Date(dateStr);
    if (Number.isNaN(dt.getTime())) {
      // Checked for date
      return null;
    }
    return dt;
  } catch {
    return null;
  }
};

const formatText = (text: string): string => {
  const date = parseDate(text);
  if (date) {
    return date.toLocaleDateString('en-US');
  }
  return text;
};

const drawTextField = (
  value: string,
  page: PDFPage,
  field: PdfFormTemplateField,
  defaultFont: PDFFont,
) => {
  const mediaBox = page.getMediaBox();

  const bbox = {
    x: field.pdf_position.x,
    y: mediaBox.height - field.pdf_position.y - field.pdf_position.height,
    width: field.pdf_position.width,
    height: field.pdf_position.height,
  };

  const initialSize = 12; // starting font size
  const minSize = 6; // minimum font size we allow

  const { lines, fontSize } = wrapText(
    value,
    { width: bbox.width, height: bbox.height },
    defaultFont,
    initialSize,
    minSize,
  );

  const fontHeight = defaultFont.heightAtSize(fontSize);
  /**
   * Some experimentally discovered bullshit to center the text in the bounding box
   */
  const offset = field.pdf_position.height / 2;
  const singleLineOffset = fontHeight / 3;
  const multiLineOffset = (fontHeight * (lines.length - 1)) / 2;
  const lineY = bbox.y + offset - singleLineOffset + multiLineOffset;

  page.drawText(lines.join('\n'), {
    x: bbox.x,
    y: lineY,
    size: fontSize,
    lineHeight: fontSize,
    font: defaultFont,
  });
};

const drawImageField = async (
  imageUrl: string,
  page: PDFPage,
  field: PdfFormTemplateField,
) => {
  const mediaBox = page.getMediaBox();
  const bbox = {
    x: field.pdf_position.x,
    y: mediaBox.height - field.pdf_position.y - field.pdf_position.height,
    width: field.pdf_position.width,
    height: field.pdf_position.height,
  };

  const imageBlob = await fetch(imageUrl).then((res) => res.blob());
  const imageBuffer = await imageBlob.arrayBuffer();

  let image = null;
  if (imageBlob.type === 'image/png') {
    image = await page.doc.embedPng(imageBuffer);
  } else if (
    imageBlob.type === 'image/jpg' ||
    imageBlob.type === 'image/jpeg'
  ) {
    image = await page.doc.embedJpg(imageBuffer);
  }

  if (!image) {
    return;
  }

  const { width, height } = image.size();
  const scaled = image.scale(
    Math.min(bbox.width / width, bbox.height / height),
  );
  page.drawImage(image, {
    x: bbox.x,
    y: bbox.y,
    width: scaled.width,
    height: scaled.height,
  });
};

const getValueForField = (
  field: PdfFormTemplateField,
  mergeData: Record<string, string | undefined>,
) => {
  if (field.id in mergeData) {
    return mergeData[field.id] || '';
  }

  return '';
};

// Updated drawField: uses wrapText with best-effort fitting.
const drawField = async (
  page: PDFPage,
  field: PdfFormTemplateField,
  defaultFont: PDFFont,
  mergeData: Record<string, string | undefined>,
) => {
  if (field.field_type === 'IMAGE' && field.field_source_image_id) {
    const { data: image } = await store.dispatch(
      enhancedApi.endpoints.pdfTemplateImagesRetrieve.initiate({
        imageId: field.field_source_image_id,
      }),
    );

    if (!image) {
      return;
    }

    await drawImageField(image.image_url, page, field);
    return;
  }

  if (field.field_type === 'TEXT') {
    const value = getValueForField(field, mergeData);
    drawTextField(formatText(value), page, field, defaultFont);
  }
};

export const modifyPdf = async ({
  pdfBlob,
  placedFields,
  mergeData,
}: ModifyPdfProps): Promise<Blob> => {
  const pdfBytes = await pdfBlob.arrayBuffer();
  const pdfDoc = await PDFDocument.load(pdfBytes, {
    ignoreEncryption: true,
  });
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

  await Promise.all(
    placedFields.map(async (field) => {
      const page = pdfDoc.getPage(field.pdf_page_number);
      await drawField(page, field, font, mergeData);
    }),
  );

  const modifiedPdfBytes = await pdfDoc.save();
  return new Blob([modifiedPdfBytes], { type: 'application/pdf' });
};
